import React, { useEffect, useRef, useState } from 'react';
import anime from 'animejs';
import { DriveMode, SpeedUnit } from '../../types';
import { IceEcoSpeedometer } from './IceEcoSpeedometer';
import { IceComfortSpeedometer } from './IceComfortSpeedometer';
import { IceSportSpeedometer } from './IceSportSpeedometer';
import { EvEcoSpeedometer } from './EvEcoSpeedometer';
import { EvComfortSpeedometer } from './EvComfortSpeedometer';
import { EvSportSpeedometer } from './EvSportSpeedometer';

export type SpeedometerType =
  | 'ICE_ECO'
  | 'ICE_COMFORT'
  | 'ICE_SPORT'
  | 'EV_ECO'
  | 'EV_COMFORT'
  | 'EV_SPORT';

interface SpeedometerHubProps {
  speed: number;
  speedUnit: SpeedUnit;
  acceleration: number;
  rpm?: number;
  gear?: number;
  isEV: boolean;
  driveMode: DriveMode;
  maxSpeed?: number;
}

export const SpeedometerHub: React.FC<SpeedometerHubProps> = ({
  speed,
  speedUnit,
  acceleration,
  rpm,
  gear,
  isEV,
  driveMode,
  maxSpeed,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Compute desired speedometer type from current vehicle configuration
  const targetType: SpeedometerType = isEV
    ? driveMode === 'SPORT'
      ? 'EV_SPORT'
      : driveMode === 'ECO'
      ? 'EV_ECO'
      : 'EV_COMFORT'
    : driveMode === 'SPORT'
    ? 'ICE_SPORT'
    : driveMode === 'ECO'
    ? 'ICE_ECO'
    : 'ICE_COMFORT';

  const [activeType, setActiveType] = useState<SpeedometerType>(targetType);
  const prevTypeRef = useRef<SpeedometerType>(targetType);

  // Anime.js 600ms geometric transition between speedometer styles
  useEffect(() => {
    if (targetType === prevTypeRef.current) return;
    prevTypeRef.current = targetType;

    if (!containerRef.current) {
      setActiveType(targetType);
      return;
    }

    const timeline = anime.timeline({
      easing: 'easeInOutCubic',
      duration: 300,
    });

    timeline
      .add({
        targets: containerRef.current,
        scale: [1, 0.92],
        opacity: [1, 0.2],
        duration: 250,
        complete: () => {
          setActiveType(targetType);
        },
      })
      .add({
        targets: containerRef.current,
        scale: [0.92, 1],
        opacity: [0.2, 1],
        duration: 350,
        easing: 'easeOutBack',
      });
  }, [targetType]);

  const renderActiveSpeedometer = () => {
    switch (activeType) {
      case 'ICE_ECO':
        return (
          <IceEcoSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 220}
          />
        );
      case 'ICE_COMFORT':
        return (
          <IceComfortSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 260}
          />
        );
      case 'ICE_SPORT':
        return (
          <IceSportSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            rpm={rpm}
            gear={gear}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 280}
          />
        );
      case 'EV_ECO':
        return (
          <EvEcoSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 220}
          />
        );
      case 'EV_COMFORT':
        return (
          <EvComfortSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 240}
          />
        );
      case 'EV_SPORT':
        return (
          <EvSportSpeedometer
            speed={speed}
            speedUnit={speedUnit}
            acceleration={acceleration}
            maxSpeed={maxSpeed || 260}
          />
        );
    }
  };

  return (
    <div ref={containerRef} className="flex items-center justify-center will-change-transform">
      {renderActiveSpeedometer()}
    </div>
  );
};
